<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import * as THREE from "three";

const emit = defineEmits<{
  done: [];
}>();

const wrap = ref<HTMLDivElement | null>(null);
const shadowWrap = ref<HTMLDivElement | null>(null);
const audioEl = ref<HTMLAudioElement | null>(null);
const started = ref(false);
const tapCount = ref(0);
const touchPrimary = ref(false);
const finishedBar = ref(false);
const barProgress = ref(0);
const audioStatus = ref<string>("");
const shadowAsDisc = ref(false);

// "Toque para falar" joke layer that appears after the loader cue ends.
// State machine: idle -> recording -> uploading -> thanks -> idle.
const recPhase = ref<"hidden" | "idle" | "recording" | "uploading" | "thanks" | "denied">("hidden");
let mediaRecorder: MediaRecorder | null = null;
let mediaStream: MediaStream | null = null;
let recordedChunks: Blob[] = [];
let recStopTimer = 0;

// Audio reactivity: feed the <audio> through an AnalyserNode so the final
// ring can pulse with Samantha's "hello, I'm here" instead of sitting
// still. audioPulse is a smoothed 0..1 averaged across the frequency
// bins, sampled every animation frame.
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let analyserData: Uint8Array | null = null;
let audioPulse = 0;

// Hold the loader on its final ring state (skip emit('done')) until BRT
// noon on 2026-05-17 (= 15:00 UTC) so the page can be shared as a
// self-contained art piece. After this instant the loader transitions
// out normally. Belt-and-braces with App.vue's /home route gate.
const KEEP_OPEN_UNTIL_MS = Date.UTC(2026, 4, 17, 15, 0, 0);

// Bumped from 500 -> 800 so the internal buffer stays sharp when the
// canvas display reaches ~1000px on desktop / ~1440px on mobile.
const canvassize = 800;

const length = 30;
const radius = 5.6;

// Slow base spin: most of the load is a near-still ribbon turning gently,
// and only the final stretch of the 20s audio cue accelerates into the ring.
const rotatevalue = 0.009;
let acceleration = 0;
let animatestep = 0;
let toend = false;

const pi2 = Math.PI * 2;

const group = new THREE.Group();
let mesh!: THREE.Mesh<THREE.TubeGeometry, THREE.MeshBasicMaterial>;
let ringcover!: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
let ring!: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;

// Shadow scene: a second THREE renderer painting the same ribbon+ring with a
// dark material on a transparent canvas; CSS scaleY squashes it and blur()
// softens it so the shadow reads as the light hitting the ribbon projected
// flat onto the floor. At the end (.shadow-final) the squash relaxes back
// to 1 so the shadow becomes a true circle, same width as the main ring.
const shadowGroup = new THREE.Group();
let shadowMesh!: THREE.Mesh<THREE.TubeGeometry, THREE.MeshBasicMaterial>;
let shadowRingcover!: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
let shadowRing!: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
let shadowCamera!: THREE.PerspectiveCamera;
let shadowScene!: THREE.Scene;
let shadowRenderer!: THREE.WebGLRenderer;

let camera!: THREE.PerspectiveCamera;
let scene!: THREE.Scene;
let renderer!: THREE.WebGLRenderer;
let animationFrame = 0;
let doneTimer = 0;
let didEmitDone = false;
let armedDoneOnAudioEnd = false;

const BG_HEX = "#E73001";
const BG_NUM = 0xe73001;

class CodepenCurve extends THREE.Curve<THREE.Vector3> {
  constructor() {
    super();
  }

  override getPoint(percent: number, target = new THREE.Vector3()) {
    const x = length * Math.sin(pi2 * percent);
    const y = radius * Math.cos(pi2 * 3 * percent);
    let t = (percent % 0.25) / 0.25;
    t = (percent % 0.25) - (2 * (1 - t) * t * -0.0185 + t * t * 0.25);
    if (Math.floor(percent / 0.25) === 0 || Math.floor(percent / 0.25) === 2) {
      t *= -1;
    }
    const z = radius * Math.sin(pi2 * 2 * (percent - t));
    return target.set(x, y, z);
  }
}

onMounted(async () => {
  await nextTick();
  if (!wrap.value) return;

  touchPrimary.value =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  // Visit ping — server logs IP (from X-Forwarded-For) and emails the
  // dev-user once per IP per hour. Fire-and-forget; never blocks render.
  try {
    fetch("/api/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        ua: navigator.userAgent || "",
        path: location.pathname || "/",
        ref: document.referrer || "",
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      }),
    }).catch(() => {});
  } catch {
    /* never fail on a tracking ping */
  }

  // FOV 35° instead of the codepen's 65°: zooms in ~2x so the ribbon
  // reads bigger without inflating the canvas display size (which on
  // mobile was producing a 1440px buffer that looked distorted/clipped
  // against the viewport).
  camera = new THREE.PerspectiveCamera(35, 1, 1, 10000);
  camera.position.z = 150;

  scene = new THREE.Scene();
  scene.add(group);

  mesh = new THREE.Mesh(
    new THREE.TubeGeometry(new CodepenCurve(), 200, 1.1, 2, true),
    new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide }),
  );
  group.add(mesh);

  ringcover = new THREE.Mesh(
    new THREE.PlaneGeometry(50, 15, 1),
    new THREE.MeshBasicMaterial({ color: BG_NUM, opacity: 0, transparent: true }),
  );
  ringcover.position.x = length + 1;
  ringcover.rotation.y = Math.PI / 2;
  group.add(ringcover);

  ring = new THREE.Mesh(
    // 128 segments instead of 32 — at the canvas sizes we hit on mobile
    // the lower count was visibly faceted on the ring's contour, even
    // with antialias on. 128 puts the seg width well under one device
    // pixel at typical DPRs.
    new THREE.RingGeometry(4.3, 5.55, 128),
    new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0, transparent: true }),
  );
  ring.position.x = length + 1.1;
  ring.rotation.y = Math.PI / 2;
  group.add(ring);

  for (let i = 0; i < 10; i++) {
    const plain = new THREE.Mesh(
      new THREE.PlaneGeometry(length * 2 + 1, radius * 3, 1),
      new THREE.MeshBasicMaterial({ color: BG_NUM, transparent: true, opacity: 0.13 }),
    );
    plain.position.z = -2.5 + i * 0.5;
    group.add(plain);
  }

  // preserveDrawingBuffer enables Clarity / Hotjar / html2canvas etc. to
  // read the WebGL framebuffer for session-replay snapshots — otherwise
  // the ribbon shows up as a blank canvas in Clarity. Costs a small
  // amount of memory bandwidth per frame; not perceptible at our scale.
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
  // No DPR cap: capping at 2 on DPR=3 Android devices left an 800*2=1600
  // backing buffer being upscaled to 720*3=2160 device pixels, which read
  // as pixelation on the final ring. Letting the full DPR through gives
  // 800*3=2400 px buffer for ~2160 device pixels → properly supersampled.
  renderer.setPixelRatio(window.devicePixelRatio);
  // updateStyle=false: keep the canvas internal buffer at 500x500 but let
  // our scoped CSS control the on-screen size — the ribbon scales 2x on
  // mobile (200vmin cap) and stays at 500px on desktop.
  renderer.setSize(canvassize, canvassize, false);
  renderer.setClearColor(BG_HEX);

  wrap.value.appendChild(renderer.domElement);

  // ── Shadow scene ──────────────────────────────────────────────────────
  shadowCamera = new THREE.PerspectiveCamera(35, 1, 1, 10000);
  shadowCamera.position.z = 150;
  shadowScene = new THREE.Scene();
  shadowScene.add(shadowGroup);

  const SHADOW_COLOR = 0x150400;

  shadowMesh = new THREE.Mesh(
    new THREE.TubeGeometry(new CodepenCurve(), 200, 1.1, 2, true),
    new THREE.MeshBasicMaterial({
      color: SHADOW_COLOR,
      transparent: true,
      opacity: 0.92,
      side: THREE.DoubleSide,
    }),
  );
  shadowGroup.add(shadowMesh);

  shadowRingcover = new THREE.Mesh(
    new THREE.PlaneGeometry(50, 15, 1),
    new THREE.MeshBasicMaterial({ color: BG_NUM, opacity: 0, transparent: true }),
  );
  shadowRingcover.position.x = length + 1;
  shadowRingcover.rotation.y = Math.PI / 2;
  shadowGroup.add(shadowRingcover);

  shadowRing = new THREE.Mesh(
    new THREE.RingGeometry(4.3, 5.55, 128),
    new THREE.MeshBasicMaterial({
      color: SHADOW_COLOR,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
    }),
  );
  shadowRing.position.x = length + 1.1;
  shadowRing.rotation.y = Math.PI / 2;
  shadowGroup.add(shadowRing);

  shadowRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  shadowRenderer.setPixelRatio(window.devicePixelRatio);
  shadowRenderer.setSize(canvassize, canvassize, false);
  shadowRenderer.setClearColor(0x000000, 0);
  shadowWrap.value?.appendChild(shadowRenderer.domElement);

  // ── Audio prime ───────────────────────────────────────────────────────
  // Browsers allow audio playback without a user gesture *only* when the
  // element is muted. Prime the <audio> so the first user tap can just flip
  // muted=false on an already-playing stream (no fresh play() call needed,
  // which is what Chrome mobile blocks on low-MEI domains).
  const a = audioEl.value;
  if (a) {
    a.muted = true;
    a.volume = 0.78;
    const tryPrime = () => {
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          audioStatus.value = "muted prime falhou";
        });
      }
    };
    if (a.readyState >= 2) {
      tryPrime();
    } else {
      a.addEventListener("loadeddata", tryPrime, { once: true });
    }
  }

  animate();
});

onBeforeUnmount(() => {
  window.cancelAnimationFrame(animationFrame);
  window.clearTimeout(doneTimer);
  window.clearTimeout(recStopTimer);
  if (audioCtx) {
    void audioCtx.close().catch(() => {});
    audioCtx = null;
    analyser = null;
    analyserData = null;
  }
  if (mediaRecorder && mediaRecorder.state === "recording") {
    try {
      mediaRecorder.stop();
    } catch {
      /* ignore */
    }
  }
  cleanupMediaStream();
  if (audioEl.value) {
    audioEl.value.pause();
  }
  renderer?.dispose();
  shadowRenderer?.dispose();
  mesh?.geometry.dispose();
  mesh?.material.dispose();
  ringcover?.geometry.dispose();
  ringcover?.material.dispose();
  ring?.geometry.dispose();
  ring?.material.dispose();
  shadowMesh?.geometry.dispose();
  shadowMesh?.material.dispose();
  shadowRingcover?.geometry.dispose();
  shadowRingcover?.material.dispose();
  shadowRing?.geometry.dispose();
  shadowRing?.material.dispose();
});

function start() {
  if (started.value) return;

  // Desktop (mouse / keyboard primary): a single click is a strong
  // enough gesture for play() to succeed, so skip the two-tap dance
  // and go straight to the audible-start path.
  if (!touchPrimary.value) {
    tapCount.value = 2;
    started.value = true;
    toend = true;
    startLoadingSound();
    requestFullscreen();
    return;
  }

  // Touch primary (Android Chrome / iOS Safari): keep the two-tap
  // dance — Android Chrome's NotAllowedError on the first tap is
  // sticky even with the muted-autoplay primer.
  if (tapCount.value === 0) {
    tapCount.value = 1;
    const el = audioEl.value;
    if (el && el.paused) {
      el.muted = true;
      el.play().catch(() => {});
    }
    return;
  }

  tapCount.value = 2;
  started.value = true;
  toend = true;
  startLoadingSound();
  requestFullscreen();
}

async function toggleRecording() {
  if (recPhase.value === "uploading") return;
  if (recPhase.value === "recording") {
    finishRecording();
    return;
  }
  if (recPhase.value !== "idle" && recPhase.value !== "thanks" && recPhase.value !== "denied") {
    return;
  }
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    recPhase.value = "denied";
    return;
  }
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    recPhase.value = "denied";
    return;
  }
  try {
    recordedChunks = [];
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
        ? "audio/ogg;codecs=opus"
        : "";
    mediaRecorder = mime
      ? new MediaRecorder(mediaStream, { mimeType: mime })
      : new MediaRecorder(mediaStream);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunks.push(e.data);
    };
    mediaRecorder.onstop = uploadRecording;
    mediaRecorder.start();
    recPhase.value = "recording";
    // Auto-stop after 15s so we don't accidentally record half an hour.
    recStopTimer = window.setTimeout(finishRecording, 15000);
  } catch (err) {
    console.warn("[loader] mediaRecorder failed:", err);
    cleanupMediaStream();
    recPhase.value = "denied";
  }
}

function finishRecording() {
  if (mediaRecorder && mediaRecorder.state === "recording") {
    mediaRecorder.stop();
  }
  window.clearTimeout(recStopTimer);
  recStopTimer = 0;
}

async function uploadRecording() {
  recPhase.value = "uploading";
  cleanupMediaStream();
  const mime = mediaRecorder?.mimeType || "audio/webm";
  const blob = new Blob(recordedChunks, { type: mime });
  recordedChunks = [];
  mediaRecorder = null;
  try {
    await fetch("/api/record", {
      method: "POST",
      headers: { "Content-Type": mime },
      body: blob,
    });
    recPhase.value = "thanks";
  } catch (err) {
    console.warn("[loader] upload failed:", err);
    recPhase.value = "denied";
  }
  // Reset back to idle after a beat so a second visitor can record without
  // refreshing the page.
  window.setTimeout(() => {
    if (recPhase.value === "thanks") recPhase.value = "idle";
  }, 2400);
}

function cleanupMediaStream() {
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
    mediaStream = null;
  }
}

function requestFullscreen() {
  if (typeof document === "undefined") return;
  const root = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const req = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  if (!req) return;
  try {
    const r = req();
    if (r && typeof (r as Promise<void>).catch === "function") {
      (r as Promise<void>).catch(() => {
        // Fullscreen rejected (user denied, iframe restriction, etc) —
        // not worth surfacing to UI, the loader still plays.
      });
    }
  } catch {
    /* ignore */
  }
}

function setupAudioAnalyser(el: HTMLAudioElement) {
  if (analyser) return;
  try {
    const Ctx =
      (window as typeof window & { AudioContext?: typeof AudioContext }).AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audioCtx = new Ctx();
    const source = audioCtx.createMediaElementSource(el);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.82;
    analyserData = new Uint8Array(analyser.frequencyBinCount);
    // Important: chain source -> analyser -> destination so the user
    // still hears the audio. Without the destination connection, the
    // analyser steals the audio and the page goes silent.
    source.connect(analyser);
    analyser.connect(audioCtx.destination);
    if (audioCtx.state === "suspended") {
      void audioCtx.resume();
    }
  } catch (err) {
    console.warn("[loader] analyser setup failed:", err);
  }
}

function startLoadingSound() {
  const el = audioEl.value;
  if (!el) return;
  setupAudioAnalyser(el);
  try {
    el.volume = 0.78;
    el.muted = false;
    audioStatus.value = "loading";

    // Android Chrome was ignoring `el.currentTime = 0` when the element
    // was already mid-stream from the muted autoplay primer, so the
    // audible playback kept the offset from the silent run and the user
    // only heard ~15s instead of the full 20s. el.load() forces a hard
    // reset of the media element (currentTime back to 0, fresh buffer)
    // and we wait for `canplay` before issuing the audible play() — that
    // way both desktop and mobile start at the exact same byte.
    el.pause();
    el.load();

    const startPlay = () => {
      // Re-seek to 0 *after* load() has reset the element. Android
      // Chrome was holding the previous offset from the muted primer
      // even after currentTime=0 pre-load; setting it again post-load
      // is what actually rewinds the stream.
      try {
        el.currentTime = 0;
      } catch {
        /* iOS sometimes throws InvalidStateError if metadata isn't
           loaded yet — canplay should guarantee it, but harmless. */
      }
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch((err: unknown) => {
          const name = err instanceof Error ? err.name : String(err);
          audioStatus.value = `blocked: ${name}`;
          console.warn("[loader] audio playback rejected:", err);
        });
      }
    };

    el.addEventListener("error", () => (audioStatus.value = "error: load"), { once: true });
    el.addEventListener("ended", () => {
      // After the cue ends, reveal the "toque para falar" prompt so the
      // viewer can leave a voice message for the dev-user to listen to
      // later. Only fires once per session.
      if (recPhase.value === "hidden") recPhase.value = "idle";
    }, { once: true });

    if (el.readyState >= 2) {
      startPlay();
    } else {
      el.addEventListener("canplay", startPlay, { once: true });
      window.setTimeout(() => {
        if (el.paused) startPlay();
      }, 800);
    }
  } catch (err) {
    audioStatus.value = `init: ${err instanceof Error ? err.name : String(err)}`;
    console.warn("[loader] audio init failed:", err);
  }
}

function render() {
  // Mobile: 20s animation matched to the 20s audio.
  // Desktop: 18s animation so the circle finishes ~2s before the audio
  // ends, per the user's "should finish circle 2s before the audio ends".
  // (240 frames / (18s * 60fps) = 0.222 per frame.)
  const stepUp = touchPrimary.value ? 0.2 : 0.222;
  const stepDown = stepUp * 4;
  animatestep = Math.max(0, Math.min(240, toend ? animatestep + stepUp : animatestep - stepDown));
  acceleration = easing(animatestep, 0, 1, 240);

  if (started.value) {
    barProgress.value = Math.min(100, Math.round((animatestep / 200) * 100));
    if (barProgress.value >= 100) {
      finishedBar.value = true;
    }
  }

  if (acceleration > 0.35) {
    let progress = (acceleration - 0.35) / 0.65;
    group.rotation.y = (-Math.PI / 2) * progress;
    group.position.z = 50 * progress;
    shadowGroup.rotation.y = group.rotation.y;
    shadowGroup.position.z = group.position.z;
    progress = Math.max(0, (acceleration - 0.97) / 0.03);
    if (progress > 0 && !mesh.material.transparent) {
      mesh.material.transparent = true;
      mesh.material.needsUpdate = true;
    }
    mesh.material.opacity = 1 - progress;
    ringcover.material.opacity = ring.material.opacity = progress;
    ring.scale.x = ring.scale.y = 0.9 + 0.1 * progress;
    // Shadow follows the same ribbon -> ring fade in its own scene.
    shadowMesh.material.opacity = 0.92 * (1 - progress);
    shadowRingcover.material.opacity = progress;
    shadowRing.material.opacity = 0.88 * progress;
    shadowRing.scale.x = shadowRing.scale.y = 0.9 + 0.1 * progress;

    // Audio-reactive pulse for the final ring. Inverted from the first
    // pass per the user note: at rest the ring is a sharp solid white
    // (high opacity) and on every spoken peak it expands AND dilutes —
    // like a sound wave breathing outward. amp is the smoothed analyser
    // average pre-multiplied 2.8x so quiet dialogue still moves the
    // ring; each syllable of "hello, I'm here" should land as a kick.
    if (progress >= 0.95 && audioPulse > 0) {
      const amp = Math.min(1, audioPulse * 2.8);
      const kick = 1 + 0.4 * amp;          // 1.0 quiet -> 1.4 peak
      const op = progress * (1 - 0.45 * amp); // solid at rest, fades at peak
      ring.scale.x = ring.scale.y = (0.9 + 0.1 * progress) * kick;
      ring.material.opacity = op;
      shadowRing.scale.x = shadowRing.scale.y = (0.9 + 0.1 * progress) * kick;
      shadowRing.material.opacity = 0.88 * op;
    }
  }

  // Toggle the CSS class that lifts the shadow canvas's vertical squash
  // back to scaleY(1) when the ring is forming — at that point the shadow
  // turns into a full circle the same width as the main ring.
  if (acceleration > 0.85 && !shadowAsDisc.value) {
    shadowAsDisc.value = true;
  }

  renderer.render(scene, camera);
  shadowRenderer.render(shadowScene, shadowCamera);

  if (toend && animatestep >= 240 && !armedDoneOnAudioEnd) {
    armedDoneOnAudioEnd = true;
    if (Date.now() < KEEP_OPEN_UNTIL_MS) {
      // Preview window — the loader is the destination, not a gate.
      // Stay on the final ring/disc forever (or until the deadline).
      return;
    }
    const audio = audioEl.value;
    if (audio && !audio.ended && !audio.paused) {
      audio.addEventListener("ended", triggerDone, { once: true });
      doneTimer = window.setTimeout(triggerDone, 22000);
    } else {
      doneTimer = window.setTimeout(triggerDone, 650);
    }
  }
}

function triggerDone() {
  if (didEmitDone) return;
  didEmitDone = true;
  emit("done");
}

function animate() {
  mesh.rotation.x += rotatevalue + acceleration;
  shadowMesh.rotation.x = mesh.rotation.x;
  if (analyser && analyserData) {
    // Cast — TS 5.7's lib.dom narrowed this to Uint8Array<ArrayBuffer> but
    // the runtime accepts any Uint8Array; our buffer is backed by a plain
    // ArrayBuffer anyway.
    analyser.getByteFrequencyData(analyserData as unknown as Uint8Array<ArrayBuffer>);
    let sum = 0;
    for (let i = 0; i < analyserData.length; i++) sum += analyserData[i];
    audioPulse = sum / (analyserData.length * 255);
  }
  render();
  animationFrame = window.requestAnimationFrame(animate);
}

// easeInCubic: dropped from easeInQuart so the ring snaps in earlier in
// the cue (was finishing in the last ~0.5s; now starts forming around
// the 14s mark of a 20s window). User wanted the circle to appear faster.
function easing(t: number, _b: number, c: number, d: number) {
  const x = Math.min(1, Math.max(0, t / d));
  return c * x * x * x;
}
</script>

<template>
  <section class="loader-screen" @pointerdown="start">
    <div id="wrap" ref="wrap"></div>
    <div
      ref="shadowWrap"
      class="shadow-wrap"
      :class="{ 'shadow-wrap--final': shadowAsDisc }"
      aria-hidden="true"
    ></div>
    <div
      class="loading-bar"
      :class="{
        'loading-bar--visible': started && recPhase === 'hidden',
        'loading-bar--done': finishedBar || recPhase !== 'hidden',
      }"
      aria-hidden="true"
    >
      <div class="loading-bar__fill" :style="{ width: `${barProgress}%` }"></div>
    </div>
    <p class="prompt" :class="{ 'prompt--hidden': started }">
      {{ touchPrimary
        ? (tapCount === 0 ? 'Toque 2x na tela' : 'Toque mais uma vez')
        : 'Toque na tela' }}
    </p>
    <!-- audio status pane hidden on the public preview; errors only
         surface in the dev console. -->
    <!-- <p v-if="started && audioStatus" class="audio-status">{{ audioStatus }}</p> -->
    <audio ref="audioEl" preload="auto" autoplay muted playsinline>
      <source src="/audio/os1-loading.mp3" type="audio/mpeg" />
    </audio>

    <!-- After the cue ends, the viewer is invited to leave a voice
         message. State machine: idle -> recording -> uploading -> thanks. -->
    <div
      v-if="recPhase !== 'hidden'"
      class="rec"
      :class="`rec--${recPhase}`"
      @pointerdown.stop="toggleRecording"
    >
      <span class="rec__dot" aria-hidden="true"></span>
      <span class="rec__label">
        <template v-if="recPhase === 'idle'">Toque para falar</template>
        <template v-else-if="recPhase === 'recording'">Gravando… toque pra parar</template>
        <template v-else-if="recPhase === 'uploading'">Enviando…</template>
        <template v-else-if="recPhase === 'thanks'">Obrigado</template>
        <template v-else-if="recPhase === 'denied'">Microfone negado</template>
      </span>
    </div>
  </section>
</template>

<style scoped>
.loader-screen {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow: hidden;
  background: #e73001;
  cursor: pointer;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}

#wrap {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* Canvas display size: 500px on desktop, 200vmin on mobile. The 2x
   ribbon comes from a narrower camera FOV (35°) instead of a giant
   canvas — that approach got us distortion-free scaling without a
   1440px buffer fighting the viewport. aspect-ratio:1/1 still
   enforced as a guard against the oval-ring bug. */
#wrap :deep(canvas) {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(500px, 200vmin);
  /* No explicit height — aspect-ratio drives it from the width. Setting
     both width and height in vmin made the ring read oval on mobile
     because the canvas display dims were coming out non-equal in
     practice; letting aspect-ratio compute height guarantees a square. */
  aspect-ratio: 1 / 1;
  margin: 0;
  transform: translate(-50%, -50%);
  display: block;
}

@media (max-width: 768px) {
  #wrap :deep(canvas) {
    width: 200vmin;
  }
}

/* Shadow wrap holds a second THREE canvas. CSS scaleY squashes the
   whole render so the ribbon-shaped shadow reads as flat against a
   floor, and filter:blur softens it. On .shadow-wrap--final the squash
   relaxes so the shadow ring renders as a true circle, same width as
   the main ring. */
.shadow-wrap {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.shadow-wrap :deep(canvas) {
  position: absolute;
  left: 50%;
  top: 78%;
  width: min(500px, 200vmin);
  aspect-ratio: 1 / 1;
  margin: 0;
  transform: translate(-50%, -50%) scaleY(0.22);
  filter: blur(13px);
  opacity: 0.35;
  display: block;
  transition: transform 900ms ease, filter 900ms ease, opacity 900ms ease;
}

.shadow-wrap--final :deep(canvas) {
  /* Even more inclined / flatter at the end (scaleY 0.22 -> 0.14) and
     a touch more transparent. Keeps the floor-shadow perspective. */
  transform: translate(-50%, -50%) scaleY(0.14);
  filter: blur(9px);
  opacity: 0.22;
}

@media (max-width: 768px) {
  .shadow-wrap :deep(canvas) {
    width: 200vmin;
  }
}

.loading-bar {
  position: absolute;
  left: 50%;
  /* Sits just under the shadow during the cue. After the audio ends the
     "toque para falar" pill takes this exact spot (top: 91%) — the bar
     fades out via `loading-bar--done` so there's no overlap. */
  top: 91%;
  width: min(420px, 80vmin);
  height: 3px;
  transform: translateX(-50%);
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.22);
  opacity: 0;
  transition: opacity 380ms ease;
  pointer-events: none;
}

.loading-bar--visible {
  opacity: 1;
}

.loading-bar--done {
  opacity: 0;
}

.loading-bar__fill {
  height: 100%;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.95);
  transition: width 160ms linear;
}

.prompt {
  position: absolute;
  left: 0;
  right: 0;
  top: 84%;
  margin: 0;
  font-family: var(--os1-font-sans, system-ui, sans-serif);
  font-size: 11px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
  text-align: center;
  pointer-events: none;
  transition: opacity 320ms ease;
  animation: prompt-pulse 2.4s ease-in-out infinite;
}

.prompt--hidden {
  opacity: 0;
  animation: none;
}

@keyframes prompt-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

.audio-status {
  position: absolute;
  left: 0;
  right: 0;
  top: 90%;
  margin: 0;
  padding: 0 16px;
  font-family: var(--os1-font-mono, ui-monospace, monospace);
  font-size: 10px;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  pointer-events: none;
}

.audio-status--error {
  color: rgba(255, 220, 200, 0.95);
}

/* Tap-to-talk affordance that appears after the audio cue ends. */
.rec {
  position: absolute;
  left: 50%;
  top: 91%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  font-family: var(--os1-font-sans, system-ui, sans-serif);
  font-size: 11px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: rec-fade-in 600ms ease both;
}

.rec__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  transition: background 220ms ease, box-shadow 220ms ease;
}

.rec--recording .rec__dot {
  background: #ff6452;
  box-shadow: 0 0 12px rgba(255, 100, 82, 0.85);
  animation: rec-pulse 1s ease-in-out infinite;
}

.rec--uploading .rec__dot,
.rec--thanks .rec__dot {
  background: rgba(255, 255, 255, 0.9);
}

.rec--denied {
  border-color: rgba(255, 200, 180, 0.4);
  color: rgba(255, 220, 200, 0.95);
}

@keyframes rec-fade-in {
  from { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
}

@keyframes rec-pulse {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.35); opacity: 0.6; }
}
</style>
